import React from 'react';
import { motion } from 'framer-motion';
import { HiArrowRight, HiPlay, HiSparkles } from 'react-icons/hi';
import { FaRocket, FaChartLine, FaBullhorn, FaTrophy, FaStar } from 'react-icons/fa';

const Hero = () => {
  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to get started with your digital marketing services.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };
  const floatingAnimation = {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <section id="home" className="relative min-h-screen overflow-hidden bg-gradient-to-br from-white via-slate-50 to-blue-50/30">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Sophisticated Gradient Orbs */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.15, 0.25, 0.15],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-10 -left-20 w-[600px] h-[600px] bg-gradient-to-br from-blue-300/40 via-purple-300/30 to-transparent rounded-full blur-[100px]"
        />
        <motion.div
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.2, 0.3, 0.2],
            x: [0, -60, 0],
            y: [0, -40, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 3 }}
          className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-indigo-300/40 via-pink-300/30 to-transparent rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            scale: [1.1, 1, 1.1],
            opacity: [0.18, 0.28, 0.18],
            rotate: [0, 90, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut", delay: 5 }}
          className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-gradient-to-r from-cyan-300/35 via-blue-300/25 to-transparent rounded-full blur-[110px]"
        />

        {/* Animated Grid Pattern */}
        <motion.div
          animate={{ opacity: [0.03, 0.06, 0.03] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiM2MzY2RjEiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzR2MmgtMTJ2LTJoMTJ6TTM2IDI0djJIMjR2LTJoMTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"
        />

        {/* Floating Sparkles */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-30, 30, -30],
              x: [-20, 20, -20],
              opacity: [0, 0.6, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 4 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut",
            }}
          >
            <HiSparkles className="text-blue-400" size={8 + Math.random() * 8} />
          </motion.div>
        ))}

        {/* Subtle Dots */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`dot-${i}`}
            className="absolute w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-15, 15, -15],
              opacity: [0.1, 0.4, 0.1],
              scale: [0.8, 1.2, 0.8],
            }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[85vh]">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center lg:text-left"
          >
            {/* Premium Badge with Animation */}
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/80 backdrop-blur-md rounded-full border border-blue-200/50 shadow-lg shadow-blue-100/50 mb-6 lg:mb-8"
            >
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gradient-to-r from-blue-600 to-purple-600"></span>
              </span>
              <FaTrophy className="text-amber-500" size={14} />
              <span className="text-slate-800 text-sm font-semibold">Award-Winning Digital Agency</span>
              <FaStar className="text-amber-400" size={12} />
            </motion.div>

            {/* Main Heading with Creative Typography */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.1] mb-6 lg:mb-8"
            >
              <span className="block text-slate-900">
                Transform Your
              </span>
              <span className="block mt-2 lg:mt-3">
                <span className="relative inline-block">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-red-500 to-orange-600 relative z-10">
                    Digital Presence
                  </span>
                  <motion.span
                    animate={{
                      scale: [1, 1.02, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 blur-2xl opacity-30"
                  />
                </span>
              </span>
              <span className="block mt-2 lg:mt-3 text-slate-900">
                Into Revenue
              </span>
            </motion.h1>

            {/* Enhanced Description */}
            <motion.p
              variants={itemVariants}
              className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 mb-8 lg:mb-10 leading-relaxed font-medium"
            >
              We craft data-driven marketing strategies that don't just look good — they
              <span className="text-red-600 font-semibold"> deliver real results</span>.
              Experience growth that matters.
            </motion.p>

            {/* Premium CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10 lg:mb-12"
            >
              <motion.button
                onClick={openWhatsApp}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group relative px-8 py-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white font-bold rounded-full shadow-2xl shadow-red-500/40 overflow-hidden cursor-pointer"
              >
                <motion.div
                  animate={{
                    x: [-200, 200],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                />
                <span className="relative flex items-center justify-center gap-2">
                  Get Started Free
                  <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group px-8 py-4 bg-white/90 backdrop-blur-md border-2 border-slate-200 text-slate-800 font-bold rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 hover:border-red-300"
              >
                <span className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <HiPlay className="text-white ml-0.5" size={20} />
                </span>
                Watch Demo
              </motion.button>
            </motion.div>

            {/* Enhanced Stats with Icons */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-6 lg:gap-8 pt-8 lg:pt-10 border-t border-slate-200/60"
            >
              {[
                { number: '500+', label: 'Success Stories', icon: FaTrophy, color: 'from-amber-500 to-orange-500' },
                { number: '98%', label: 'Client Satisfaction', icon: FaStar, color: 'from-red-600 to-red-500' },
                { number: '3.5x', label: 'Avg ROI Increase', icon: FaChartLine, color: 'from-orange-600 to-red-600' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center lg:text-left group"
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                    <div className={`w-8 h-8 bg-gradient-to-r ${stat.color} rounded-lg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <stat.icon className="text-white" size={14} />
                    </div>
                  </div>
                  <div className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-1">{stat.number}</div>
                  <div className="text-slate-600 text-sm font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Enhanced 3D Cards with More Animations */}
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block h-[600px]"
          >
            {/* Main Dashboard Card with Enhanced Design */}
            <motion.div
              animate={floatingAnimation}
              className="relative z-10"
            >
              <div className="bg-white/90 backdrop-blur-2xl rounded-3xl p-8 border border-white/60 shadow-2xl hover:shadow-3xl transition-shadow">
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 relative overflow-hidden">
                  {/* Animated Background Pattern */}
                  <motion.div
                    animate={{ opacity: [0.05, 0.1, 0.05] }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNGRkZGRkYiPjxwYXRoIGQ9Ik0zNiAzNHYyaC0xMnYtMmgxMnpNMzYgMjR2MkgyNHYtMmgxMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"
                  />

                  {/* Dashboard Header */}
                  <div className="flex items-center justify-between mb-6 relative z-10">
                    <div className="flex items-center gap-3">
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                        className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg"
                      >
                        <FaChartLine className="text-white" size={20} />
                      </motion.div>
                      <div>
                        <div className="text-white font-bold text-lg">Growth Analytics</div>
                        <div className="text-emerald-400 text-sm font-medium flex items-center gap-1">
                          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                          Live Data
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <motion.div whileHover={{ scale: 1.2 }} className="w-3 h-3 rounded-full bg-red-500 cursor-pointer" />
                      <motion.div whileHover={{ scale: 1.2 }} className="w-3 h-3 rounded-full bg-yellow-500 cursor-pointer" />
                      <motion.div whileHover={{ scale: 1.2 }} className="w-3 h-3 rounded-full bg-green-500 cursor-pointer" />
                    </div>
                  </div>

                  {/* Enhanced Chart with Gradient Bars */}
                  <div className="h-48 flex items-end gap-2 mb-6 relative z-10">
                    {[45, 68, 52, 88, 62, 95, 75, 92, 68, 98, 82, 96].map((height, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 relative group"
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        whileHover={{ scale: 1.1 }}
                        transition={{ delay: 1 + i * 0.08, duration: 0.6, ease: "backOut" }}
                      >
                        <div className="w-full h-full bg-gradient-to-t from-blue-600 via-purple-600 to-pink-500 rounded-t-xl relative overflow-hidden">
                          <motion.div
                            animate={{ y: ['100%', '-100%'] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            className="absolute inset-0 bg-gradient-to-t from-transparent via-white/20 to-transparent"
                          />
                        </div>
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white px-2 py-1 rounded text-xs font-bold text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          +{height}%
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Enhanced Stats Grid */}
                  <div className="grid grid-cols-3 gap-4 relative z-10">
                    {[
                      { label: 'Traffic', value: '+147%', color: 'from-emerald-500 to-green-600', icon: '📈' },
                      { label: 'Leads', value: '+89%', color: 'from-purple-500 to-indigo-600', icon: '🎯' },
                      { label: 'Revenue', value: '+234%', color: 'from-pink-500 to-rose-600', icon: '💰' },
                    ].map((stat, i) => (
                      <motion.div
                        key={i}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/15 transition-all cursor-pointer border border-white/10"
                        whileHover={{ y: -4, scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="text-2xl mb-2">{stat.icon}</div>
                        <div className={`text-xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-1`}>
                          {stat.value}
                        </div>
                        <div className="text-white/70 text-xs font-medium">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Enhanced Floating Cards */}
            <motion.div
              animate={{
                y: [-8, 12, -8],
                rotate: [-3, 3, -3],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-8 -left-8 bg-white rounded-2xl p-5 shadow-2xl hover:shadow-3xl transition-shadow border border-slate-100 z-20"
              whileHover={{ scale: 1.05, y: -10 }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg"
                >
                  <FaRocket className="text-white text-xl" />
                </motion.div>
                <div>
                  <div className="text-slate-500 text-sm font-medium mb-1">SEO Performance</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-emerald-600 font-black text-2xl">98</span>
                    <span className="text-slate-400 text-sm font-bold">/100</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{
                y: [12, -8, 12],
                rotate: [3, -3, 3],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-2xl hover:shadow-3xl transition-shadow border border-slate-100 z-20"
              whileHover={{ scale: 1.05, y: -10 }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-14 h-14 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg"
                >
                  <FaBullhorn className="text-white text-xl" />
                </motion.div>
                <div>
                  <div className="text-slate-500 text-sm font-medium mb-1">Campaign ROI</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-purple-600 font-black text-2xl">+324</span>
                    <span className="text-slate-400 text-lg font-bold">%</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Additional Floating Badge */}
            <motion.div
              animate={{
                y: [-5, 5, -5],
                scale: [1, 1.05, 1],
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute top-1/2 -right-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-4 shadow-2xl z-20"
            >
              <div className="text-center">
                <div className="text-white font-black text-3xl mb-1">5⭐</div>
                <div className="text-blue-100 text-xs font-semibold">Rating</div>
              </div>
            </motion.div>

            {/* Decorative Animated Rings */}
            {[1, 2, 3].map((i) => (
              <motion.div
                key={i}
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.1, 0.2, 0.1],
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 15 + i * 3,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-2 border-gradient-to-r from-blue-400 to-purple-400 rounded-full"
                style={{
                  width: `${400 + i * 80}px`,
                  height: `${400 + i * 80}px`,
                  borderColor: i % 2 === 0 ? 'rgba(147, 197, 253, 0.2)' : 'rgba(196, 181, 253, 0.2)',
                }}
              />
            ))}
          </motion.div>
        </div>
      </div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30"
      >
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 cursor-pointer group"
        >
          <span className="text-slate-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Scroll to explore
          </span>
          <div className="w-7 h-11 border-2 border-slate-400 rounded-full flex justify-center pt-2 group-hover:border-purple-500 transition-colors">
            <motion.div
              animate={{
                opacity: [1, 0, 1],
                y: [0, 14, 0]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
